/**
 * Shared card for the Extensions page's Gadget/Apps/External/Skills grids.
 * Footer-right is either an action button (Open/Launch) or a toggle switch (Skills' enabled
 * state) — never both, matching the Extensions mock's hasAction/hasToggle split.
 */

import { useState, type ComponentType } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Switch from '@mui/material/Switch'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PushPinIcon from '@mui/icons-material/PushPin'
import { ds } from './agent-builder/theme'

export interface ExtensionCardMenuItem {
  label: string
  icon: ComponentType<{ fontSize?: 'small' }>
  onClick: () => void
  danger?: boolean
  /** Renders a divider above this item, to separate it from the items before it. */
  divider?: boolean
}

export interface ExtensionCardToggle {
  checked: boolean
  onChange: () => void
}

export interface ExtensionCardProps {
  icon: ComponentType<{ fontSize?: 'small' }>
  iconColor: string
  iconBg: string
  title: string
  subtitle?: string
  description?: string
  chips?: string[]
  meta?: string
  actionLabel?: string
  actionIcon?: ComponentType<{ fontSize?: 'small' }>
  onOpen?: () => void
  toggle?: ExtensionCardToggle
  menuItems?: ExtensionCardMenuItem[]
  onCardClick?: () => void
  pinned?: boolean
}

export function ExtensionCard({
  icon: Icon, iconColor, iconBg, title, subtitle, description, chips, meta,
  actionLabel, actionIcon: ActionIcon, onOpen, toggle, menuItems, onCardClick, pinned,
}: ExtensionCardProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  return (
    <Paper
      variant="outlined"
      onClick={onCardClick}
      sx={{
        p: 2,
        borderRadius: ds.radius,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...(onCardClick && {
          cursor: 'pointer',
          transition: 'box-shadow 0.15s, border-color 0.15s',
          '&:hover': { borderColor: 'text.disabled', boxShadow: ds.shadowMd },
        }),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: ds.radiusSm,
            bgcolor: iconBg,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {(pinned || (menuItems && menuItems.length > 0)) && (
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            {pinned && <PushPinIcon sx={{ fontSize: 16, color: '#008FA8' }} />}
            {menuItems && menuItems.length > 0 && (
              <IconButton
                size="small"
                onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget) }}
                sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        )}
        {menuItems && menuItems.length > 0 && (
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            {menuItems.map(item => [
              item.divider && <Divider key={`${item.label}-divider`} />,
              <MenuItem
                key={item.label}
                onClick={e => { e.stopPropagation(); setMenuAnchor(null); item.onClick() }}
                sx={item.danger ? { color: 'error.main' } : undefined}
              >
                <ListItemIcon sx={item.danger ? { color: 'error.main' } : undefined}>
                  <item.icon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{item.label}</ListItemText>
              </MenuItem>,
            ])}
          </Menu>
        )}
      </Stack>
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {description}
        </Typography>
      )}
      {chips && chips.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {chips.map(c => <Chip key={c} size="small" label={c} variant="outlined" />)}
        </Stack>
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 'auto', pt: 0.5 }}>
        <Typography variant="caption" color="text.disabled">{meta}</Typography>
        {toggle ? (
          <Switch
            size="small"
            color="primary"
            checked={toggle.checked}
            onClick={e => e.stopPropagation()}
            onChange={toggle.onChange}
          />
        ) : actionLabel && ActionIcon && onOpen ? (
          <Button
            size="small"
            variant="outlined"
            endIcon={<ActionIcon fontSize="small" />}
            onClick={e => { e.stopPropagation(); onOpen() }}
            sx={{
              height: 30,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: ds.radiusSm,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: 'transparent',
              },
            }}
          >
            {actionLabel}
          </Button>
        ) : null}
      </Stack>
    </Paper>
  )
}
